import { useCallback, useEffect, useRef, useState } from 'react';
import type { PointsHistory, PointsRewards } from '@pons/contracts';
import { accountFetch } from '../auth/api';

export function useAccountPoints(
  accountId: string | undefined,
  visible: boolean,
) {
  const [data, setData] = useState<PointsRewards>();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [serverOffset, setServerOffset] = useState(0);
  const running = useRef(false),
    mounted = useRef(true);
  const request = useCallback(
    async (reward?: 'daily' | 'welcome', cursor?: string) => {
      if (!accountId || running.current) return;
      running.current = true;
      setBusy(true);
      setError('');
      try {
        const response = await accountFetch(
          accountId,
          cursor
            ? '/api/points/history?cursor=' + encodeURIComponent(cursor)
            : reward
              ? '/api/points/claim'
              : '/api/points/rewards',
          {
            ...(reward
              ? {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({ reward }),
                }
              : {}),
            signal: AbortSignal.timeout(15000),
            cache: 'no-store',
          },
        );
        const body = await response.json();
        if (!response.ok)
          throw Error(
            body.error || 'Points are unavailable. Please try again.',
          );
        if (!mounted.current) return;
        if (cursor) {
          const history = body as PointsHistory;
          setData(
            (previous) =>
              previous && {
                ...previous,
                history: {
                  ...history,
                  entries: [...previous.history.entries, ...history.entries],
                },
              },
          );
        } else {
          setData(body);
          setServerOffset(body.serverTime - Date.now());
        }
      } catch (cause) {
        if (mounted.current)
          setError(
            cause instanceof Error
              ? cause.message
              : 'Could not load points. Please try again.',
          );
      } finally {
        running.current = false;
        if (mounted.current) setBusy(false);
      }
    },
    [accountId],
  );
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  useEffect(() => {
    void request();
    const refresh = () => {
      if (!document.hidden) void request();
    };
    document.addEventListener('visibilitychange', refresh);
    const timer = visible ? setInterval(refresh, 30000) : undefined;
    return () => {
      document.removeEventListener('visibilitychange', refresh);
      clearInterval(timer);
    };
  }, [request, visible]);
  useEffect(() => {
    if (!data || !visible) return;
    const timer = setTimeout(
      () => void request(),
      Math.max(100, data.nextReset - data.serverTime + 100),
    );
    return () => clearTimeout(timer);
  }, [data, visible, request]);
  return {
    data,
    busy,
    error,
    serverOffset,
    formattedBalance: data
      ? new Intl.NumberFormat('en-US').format(BigInt(data.balance))
      : '—',
    claim: (reward: 'daily' | 'welcome') => void request(reward),
    refresh: () => void request(),
    loadMore: () => {
      if (data?.history.nextCursor)
        void request(undefined, data.history.nextCursor);
    },
  };
}
export type AccountPoints = ReturnType<typeof useAccountPoints>;
